import QueryString from 'query-string';
import { toastr } from 'react-redux-toastr';
import { push } from 'react-router-redux';
import {
  FLAG_GETTING_TICKETS,
  GET_TICKETS,
  CLEAR_TICKETS,
  GET_TICKET,
  CLEAR_TICKET,
  FLAG_TICKET_ACTIVITY,
  FLAG_CHANGING_PASSENGER,
  GET_CUSTOMER_UPDATE_HISTORY,
  CLEAR_CUSTOMER_UPDATE_HISTORY,
  FLAG_GETTING_CUSTOMER_UPDATE_HISTORY,
  GET_TICKET_FROM_ITINERARY,
  CLEAR_TICKET_FROM_ITINERARY,
  FLAG_GETTING_TICKET_FROM_ITINERARY,
  PRINT_TICKET_VOUCHER_ON_MOUNT,
} from '../types';
import {
  TICKET_ENDPOINT,
  PRINTED_TICKET_ENDPOINT,
  generateSendTicketByEmailEndpoint,
  generateSalesSessionTicketsEnpoint,
  TICKET_ENDPOINT_V2,
  generateTicketRefund,
  generateTicketChangePassengerEndpoint,
  generateTicketPostponeEndpoint,
  generatePostVoidTicket,
  generateRegisterPinPadVoidEndpoint,
  TICKET_DELETE_MODAL_ENDPOINT,
  TICKET_CUSTOMER_ENDPOINT,
  TICKET_FOR_MASSIVE_POSTPONE_ENDPOINT,
  TICKET_POSTPONE_PROCESS_ENDPOINT,
  generateTicketTaxableInformationEndpoint,
  generateTicketChangeVoucherTypeEndpoint,
  MASSIVE_TICKET_POSTPONE_MODAL_ENDPOINT,
  BASE_SECURE_URL,
  generateCustomerUpdateHistoryEndpoint,
  generatePutCustomerUpdateHistoryEndpoint,
  generateTicketChangeLocation,
} from '../../config/endpoints';
import {
  DEFAULT_GET_CONFIG,
  DEFAULT_PATCH_CONFIG,
  DEFAULT_POST_CONFIG,
  DEFAULT_PUT_CONFIG,
  getDefaultConfig,
} from '../../config/rest';
import {
  handleResponseError,
  isErrorResponse,
} from '../../utils/error-handlers';
import { printPrintedTicket } from '../../utils/printers/Ticket';
import {
  ACCOUNTING_UNIT_PATH,
  RESERVATION_UNIT_PATH,
  TICKET_MASSIVE_POSTPONE_PATH,
  TICKET_PATH,
} from '../../config/paths';
import { getCompanyTicket } from '../contract';

const flagGettingTickets = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_TICKETS,
    payload: flag,
  });

const getTickets = async (query) => async (dispatch) => {
  try {
    dispatch(flagGettingTickets(true));

    const url = `${TICKET_ENDPOINT}?${QueryString.stringify(query)}`;
    const response = await fetch(url, DEFAULT_GET_CONFIG);

    await isErrorResponse(response, null, dispatch);

    const tickets = await response.json();

    dispatch({
      type: GET_TICKETS,
      payload: tickets,
    });
  } catch ({ message }) {
    toastr.error('Error', message);
  } finally {
    dispatch(flagGettingTickets(false));
  }
};

const getTicketsV2 = async (query) => async (dispatch) => {
  try {
    dispatch(flagGettingTickets(true));

    const url = `${TICKET_ENDPOINT_V2}?${QueryString.stringify(query)}`;
    const response = await fetch(url, DEFAULT_GET_CONFIG);

    await isErrorResponse(response, null, dispatch);

    const tickets = await response.json();

    dispatch({
      type: GET_TICKETS,
      payload: tickets,
    });
  } catch ({ message }) {
    toastr.error('Error', message);
  } finally {
    dispatch(flagGettingTickets(false));
  }
};

const getTicketsForMassivePostpone = async (query) => async (dispatch) => {
  try {
    dispatch(flagGettingTickets(true));

    const url = `${TICKET_FOR_MASSIVE_POSTPONE_ENDPOINT}?${QueryString.stringify(
      query,
    )}`;
    const response = await fetch(url, DEFAULT_GET_CONFIG);

    await isErrorResponse(response, null, dispatch);

    const tickets = await response.json();

    dispatch({
      type: GET_TICKETS,
      payload: tickets,
    });
  } catch ({ message }) {
    toastr.error('Error', message);
  } finally {
    dispatch(flagGettingTickets(false));
  }
};

const clearTickets = () => (dispatch) =>
  dispatch({
    type: CLEAR_TICKETS,
  });

const flagTicketActivity = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_TICKET_ACTIVITY,
    payload: flag,
  });

const getTicket =
  async ({ ticketId }) =>
  async (dispatch) => {
    try {
      dispatch(flagTicketActivity(true));
      const url = `${TICKET_ENDPOINT_V2}/${ticketId}`;
      const response = await fetch(url, DEFAULT_GET_CONFIG);

      await isErrorResponse(response, null, dispatch);
      const ticket = await response.json();
      dispatch({
        type: GET_TICKET,
        payload: ticket,
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagTicketActivity(false));
    }
  };

const clearTicket = () => (dispatch) =>
  dispatch({
    type: CLEAR_TICKET,
  });

const voidTicket =
  async ({ ticketId, justification }) =>
  async (dispatch) => {
    try {
      dispatch(flagTicketActivity(true));
      const url = generatePostVoidTicket(ticketId);
      const payload = { description: justification };
      const response = await fetch(url, {
        ...DEFAULT_PATCH_CONFIG,
        body: JSON.stringify(payload),
      });
      await isErrorResponse(
        response,
        () => getTicket({ ticketId }),
        dispatch,
        'patch',
      );
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagTicketActivity(false));
    }
  };

const voidTicketWithCallback =
  async ({ ticketId, justification }, afterVoidTicket) =>
  async (dispatch) => {
    try {
      dispatch(flagTicketActivity(true));
      const url = generatePostVoidTicket(ticketId);
      const payload = { description: justification };
      const response = await fetch(url, {
        ...DEFAULT_PATCH_CONFIG,
        body: JSON.stringify(payload),
      });
      await isErrorResponse(response, 'patch');

      await dispatch(getTicket({ ticketId }));
      await afterVoidTicket();
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagTicketActivity(false));
    }
  };

const getTicketDeleteModal = async (ticketId) => async () => {
  const url = `${TICKET_DELETE_MODAL_ENDPOINT}/${ticketId}`;
  const response = await fetch(url, DEFAULT_GET_CONFIG);
  await isErrorResponse(response);
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const executeTicketVoidAction =
  async ({ url, method, ticketId, justification }) =>
  async (dispatch) => {
    try {
      dispatch(flagTicketActivity(true));

      const normalizedMethod = (method || 'patch').toLowerCase();
      const requestConfig = {
        ...getDefaultConfig(normalizedMethod),
        ...(normalizedMethod !== 'delete' && {
          body: JSON.stringify({ description: justification }),
        }),
      };

      const response = await fetch(`${BASE_SECURE_URL}${url}`, requestConfig);

      await isErrorResponse(
        response,
        () => getTicket({ ticketId }),
        dispatch,
        normalizedMethod,
      );
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagTicketActivity(false));
    }
  };

const registerPinPadVoid =
  async ({
    ticketId,
    originalTransactionId,
    operation,
    approved,
    responseCode,
    response: pinPadResponse,
  }) =>
  async () => {
    const url = generateRegisterPinPadVoidEndpoint(ticketId);
    const response = await fetch(url, {
      ...DEFAULT_POST_CONFIG,
      body: JSON.stringify({
        originalTransactionId,
        operation,
        approved,
        responseCode,
        response: pinPadResponse,
      }),
    });
    await isErrorResponse(response);
  };

const getPrintedTicket = async (ticketId) => async (dispatch) => {
  dispatch(flagTicketActivity(true));

  const url = `${PRINTED_TICKET_ENDPOINT}/${ticketId}`;

  try {
    const response = await fetch(url, DEFAULT_GET_CONFIG);

    // handle an error response from the server
    const serverError = await handleResponseError(response);
    if (serverError) {
      toastr.error('Error', serverError);
      return dispatch(flagTicketActivity(false));
    }

    const printedTicket = await response.json();
    printPrintedTicket(printedTicket);
    return dispatch(flagTicketActivity(false));
  } catch (err) {
    toastr.error('Error', 'error obteniendo boleto impreso');
    return dispatch(flagTicketActivity(false));
  }
};

const postTicketVoucherTypeChange =
  async (
    endpoint,
    {
      ticketId,
      voucherTypeId,
      businessId,
      creditNoteMotive,
      creditNoteMotiveDescription,
    },
  ) =>
  async (dispatch) => {
    try {
      dispatch(flagTicketActivity(true));

      const payload = {
        ticketId,
        voucherTypeId,
        businessId,
        creditNoteMotive,
        creditNoteMotiveDescription,
      };

      const url = generateTicketChangeVoucherTypeEndpoint(ticketId, endpoint);

      const promise = await fetch(url, {
        ...DEFAULT_POST_CONFIG,
        body: JSON.stringify(payload),
      });

      await isErrorResponse(
        promise,
        (response) => push(`${TICKET_PATH}/${response.ticketId}`),
        dispatch,
      );
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagTicketActivity(false));
    }
  };

const postPostponeTicket =
  async (
    endpoint,
    ticketId,
    { creditNoteMotive, creditNoteMotiveDescription },
    forThirdParty = false,
  ) =>
  async (dispatch) => {
    try {
      dispatch(flagTicketActivity(true));

      const payload = {
        ticketId,
        creditNoteMotive,
        creditNoteMotiveDescription,
      };

      const url = generateTicketPostponeEndpoint(ticketId, endpoint);

      const response = await fetch(url, {
        ...DEFAULT_POST_CONFIG,
        body: JSON.stringify(payload),
      });

      const callTicket = forThirdParty
        ? getCompanyTicket({ companyTicketId: ticketId })
        : getTicket({ ticketId });

      await isErrorResponse(response, () => callTicket, dispatch);
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagTicketActivity(false));
    }
  };

const successPostponeTicket = (responseList) => {
  if (responseList === null || !responseList.length) return;

  const ticketErrorPostpone = responseList.filter(
    (ticket) => !ticket.postponed,
  );
  const countSuccessPostpone = responseList.filter(
    (ticket) => ticket.postponed,
  ).length;
  if (countSuccessPostpone > 0) {
    toastr.success('Éxito', `Se postergaron ${countSuccessPostpone} boletos`);
  }
  ticketErrorPostpone.forEach((ticket) => {
    toastr.warning(
      `No se pudo postergar el boleto ID: ${ticket.ticketId}`,

      ticket.detail,
      {
        timeOut: 0,
        showCloseButton: true,
        progressBar: true,
        removeOnHover: false,
      },
    );
  });
};
const postTicketMassivePostpone =
  async ({ comment, ticketIdList, redirect = true }) =>
  async (dispatch) => {
    try {
      dispatch(flagTicketActivity(true));

      const payload = {
        comment,
        ticketIdList,
      };
      const url = TICKET_POSTPONE_PROCESS_ENDPOINT;

      const response = await fetch(url, {
        ...DEFAULT_POST_CONFIG,
        body: JSON.stringify(payload),
      });

      if (redirect) {
        dispatch(push(TICKET_MASSIVE_POSTPONE_PATH));
      }

      const NOOP_ACTION = { type: '@@ticket/NOOP' };

      await isErrorResponse(
        response,
        (res) => {
          if (res) {
            successPostponeTicket(res);
          }

          return redirect ? push(TICKET_MASSIVE_POSTPONE_PATH) : NOOP_ACTION;
        },
        dispatch,
        'post',
      );

      dispatch(clearTickets());
      if (ticketIdList.length > 0) {
        successPostponeTicket(response);
      }
    } catch ({ message }) {
      if (message) {
        toastr.error('Error', message);
      }
    } finally {
      dispatch(flagTicketActivity(false));
    }
  };

const postImmediateTicketPostpone =
  async ({
    endpoint,
    ticketIdList,
    comment,
    itineraryId = null,
    redirect = true,
  }) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingTickets(true));
      const url = `${BASE_SECURE_URL}${endpoint}`;
      const payload = JSON.stringify({ ticketIdList, comment, itineraryId });
      const response = await fetch(url, {
        ...DEFAULT_POST_CONFIG,
        body: payload,
      });
      if (redirect) {
        dispatch(push(TICKET_MASSIVE_POSTPONE_PATH));
      }

      const NOOP_ACTION = { type: '@@ticket/NOOP' };

      await isErrorResponse(
        response,
        (res) => {
          if (res) {
            successPostponeTicket(res);
          }

          return redirect ? push(TICKET_MASSIVE_POSTPONE_PATH) : NOOP_ACTION;
        },
        dispatch,
        'post',
      );

      dispatch(clearTickets());
      if (ticketIdList.length > 0) {
        successPostponeTicket(response);
      }
    } catch (error) {
      if (error) {
        toastr.error('Error', error.message);
      }
    } finally {
      dispatch(flagGettingTickets(false));
    }
  };

const postTicketMassivePostponeV2 =
  async ({ comment, ticketIdList }) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingTickets(true));
      const payload = {
        comment,
        ticketIdList,
      };
      const url = `${TICKET_POSTPONE_PROCESS_ENDPOINT}/immediate`;
      const promise = await fetch(url, {
        ...DEFAULT_POST_CONFIG,
        body: JSON.stringify(payload),
      });
      await isErrorResponse(promise, null, dispatch);
      const responseList = await promise.json();

      if (responseList.length !== null && responseList.length > 0) {
        const ticketErrorPostpone = responseList.filter(
          (ticket) => !ticket.postponed,
        );

        const ticketSuccessPostpone = responseList.filter(
          (ticket) => ticket.postponed,
        );

        if (ticketSuccessPostpone.length > 0) {
          toastr.success(
            'Éxito',
            `Se postergaron ${ticketSuccessPostpone.length} tickets`,
          );
        }

        ticketErrorPostpone.forEach((ticket) => {
          toastr.error(
            `No se pudo postergar el ticket ${ticket.ticketId}`,
            ticket.detail,
            {
              timeOut: 0,
              showCloseButton: true,
              progressBar: true,
              removeOnHover: false,
            },
          );
        });
      }

      dispatch(push(RESERVATION_UNIT_PATH));
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagGettingTickets(false));
    }
  };

const flagChangingPassenger = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_CHANGING_PASSENGER,
    payload: flag,
  });

const flagPrintTicketVoucher = (flag) => (dispatch) =>
  dispatch({
    type: PRINT_TICKET_VOUCHER_ON_MOUNT,
    payload: flag,
  });

const postChangePassenger =
  async (
    endpoint,
    ticketId,
    { newCustomerId, creditNoteMotive, creditNoteMotiveDescription },
  ) =>
  async (dispatch) => {
    try {
      dispatch(flagChangingPassenger(true));

      const payload = {
        id: newCustomerId,
        creditNoteMotive,
        creditNoteMotiveDescription,
      };

      const url = generateTicketChangePassengerEndpoint(ticketId, endpoint);

      const promise = await fetch(url, {
        ...DEFAULT_POST_CONFIG,
        body: JSON.stringify(payload),
      });

      await isErrorResponse(
        promise,
        (response) => push(`${TICKET_PATH}/${response.ticketId}`),
        dispatch,
      );
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagChangingPassenger(false));
    }
  };

const postSendTicketByEmail =
  async ({ ticketId, email }) =>
  async (dispatch) => {
    try {
      dispatch(flagTicketActivity(true));
      const url = generateSendTicketByEmailEndpoint(ticketId);
      const payload = [email];
      const response = await fetch(url, {
        ...DEFAULT_POST_CONFIG,
        body: JSON.stringify(payload),
      });
      // check if it is an error response
      await isErrorResponse(response, null, dispatch);
      // if it is a 200 response then mail has been sent correctly
      return true;
    } catch ({ message }) {
      toastr.error('Error', message);
      return false;
    } finally {
      dispatch(flagTicketActivity(false));
    }
  };

const postTicketRefund =
  async (endpoint, ticketId, forThirdParty, payload) => async (dispatch) => {
    try {
      dispatch(flagTicketActivity(true));

      const url = generateTicketRefund(ticketId, endpoint);
      const response = await fetch(url, {
        ...DEFAULT_POST_CONFIG,
        body: JSON.stringify(payload),
      });

      const callTicket = forThirdParty
        ? getCompanyTicket({ companyTicketId: ticketId })
        : getTicket({ ticketId });

      // check if it is an error response
      await isErrorResponse(response, () => callTicket, dispatch);
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagTicketActivity(false));
    }
  };

const putTicketChangeLocation =
  async (ticketId, forThirdParty, payload) => async (dispatch) => {
    try {
      dispatch(flagTicketActivity(true));

      const url = generateTicketChangeLocation(ticketId);
      const response = await fetch(url, {
        ...DEFAULT_PUT_CONFIG,
        body: JSON.stringify(payload),
      });

      const callTicket = forThirdParty
        ? getCompanyTicket({ companyTicketId: ticketId })
        : getTicket({ ticketId });

      await isErrorResponse(
        response,
        () => {
          toastr.success(
            'Exito',
            'Embarque / Desembarque cambiado correctamente',
          );
          return callTicket;
        },
        dispatch,
        'put',
      );
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagTicketActivity(false));
    }
  };

const getSalesSessionTickets =
  async ({ salesSessionId }) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingTickets(true));
      const url = generateSalesSessionTicketsEnpoint(salesSessionId);
      const response = await fetch(url, { ...DEFAULT_GET_CONFIG });
      // check if it is an error response
      await isErrorResponse(response, null, dispatch);
      const tickets = await response.json();
      dispatch({
        type: GET_TICKETS,
        payload: { content: tickets },
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagGettingTickets(false));
    }
  };

const getTicketCustomer = async (tableFilters) => async (dispatch) => {
  try {
    dispatch(flagGettingTickets(true));
    const query = QueryString.stringify(tableFilters);

    const url = `${TICKET_CUSTOMER_ENDPOINT}?${query}`;
    const response = await fetch(url, DEFAULT_GET_CONFIG);

    await isErrorResponse(response, null, dispatch);
    const tickets = await response.json();
    dispatch({
      type: GET_TICKETS,
      payload: tickets,
    });
  } catch ({ message }) {
    toastr.error('Error', message);
  } finally {
    dispatch(flagGettingTickets(false));
  }
};

const patchTicketTaxableInformation =
  async (
    ticketId,
    {
      customerId,
      firstName,
      lastName,
      identificationTypeId,
      idDocumentNumber,
      idCountryOfOrigin,
      businessId,
      name,
      businessTaxId,
    },
  ) =>
  async (dispatch) => {
    try {
      dispatch(flagTicketActivity(true));

      const payload = {
        customerId,
        firstName,
        lastName,
        identificationTypeId,
        idDocumentNumber,
        idCountryOfOrigin,
        businessId,
        name,
        businessTaxId,
        id: ticketId,
      };

      const url = generateTicketTaxableInformationEndpoint(ticketId);
      const response = await fetch(url, {
        ...DEFAULT_PATCH_CONFIG,
        body: JSON.stringify(payload),
      });

      await isErrorResponse(response, null, dispatch);
      await response.json();
      dispatch(push(ACCOUNTING_UNIT_PATH));
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagTicketActivity(false));
    }
  };

const getTicketActionModal =
  async ({ ticketId, url }) =>
  async () => {
    try {
      const promise = await fetch(`${url}/${ticketId}`, {
        ...DEFAULT_GET_CONFIG,
      });

      await isErrorResponse(promise);

      const response = await promise.json();

      return response;
    } catch (error) {
      toastr.error('Error', error.message);
      return null;
    }
  };

const postMassiveTicketActionModal =
  async ({ ticketIdList }) =>
  async () => {
    try {
      const url = MASSIVE_TICKET_POSTPONE_MODAL_ENDPOINT;
      const body = JSON.stringify(ticketIdList);
      const promise = await fetch(url, {
        ...DEFAULT_POST_CONFIG,
        body,
      });
      await isErrorResponse(promise);
      const response = await promise.json();
      return response;
    } catch (error) {
      toastr.error('Error', error.message);
      return null;
    }
  };

const putAcountingData =
  async (
    ticketId,
    {
      newPaymentMethod,
      newBusinessName,
      newBusinessTaxId,
      businessId,
      comment,
      voucherCode,
      idDocumentNumber,
      isBusiness,
    },
  ) =>
  async (dispatch) => {
    try {
      dispatch(flagTicketActivity(true));
      const url = `${TICKET_ENDPOINT}/${ticketId}/accounting-information`;
      const payload = {
        newPaymentMethod: {
          id: newPaymentMethod.value,
          name: newPaymentMethod.label,
        },
        newPaymentMethodId: newPaymentMethod.value,
        newBusinessName,
        newBusinessTaxId,
        businessId,
        comment,
        voucherCode,
        newIdDocumentNumber: idDocumentNumber,
        isBusiness,
      };
      const response = await fetch(url, {
        ...DEFAULT_PUT_CONFIG,
        body: JSON.stringify(payload),
      });
      await isErrorResponse(response, null, dispatch);
      await dispatch(getTicket({ ticketId }));
      toastr.success('Éxito', 'Datos contables actualizados correctamente');
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagTicketActivity(false));
    }
  };

const flagGettingCustomerUpdateHistory = (payload) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_CUSTOMER_UPDATE_HISTORY,
    payload,
  });

const clearCustomerUpdateHistory = () => (dispatch) =>
  dispatch({
    type: CLEAR_CUSTOMER_UPDATE_HISTORY,
  });

const getCustomerUpdateHistory = async (ticketId) => async (dispatch) => {
  try {
    dispatch(flagGettingCustomerUpdateHistory(true));
    const url = generateCustomerUpdateHistoryEndpoint(ticketId);
    const response = await fetch(url, DEFAULT_GET_CONFIG);
    await isErrorResponse(response, null, dispatch);
    const customerUpdateHistory = await response.json();
    dispatch({
      type: GET_CUSTOMER_UPDATE_HISTORY,
      payload: customerUpdateHistory,
    });
  } catch ({ message }) {
    toastr.error('Error', message);
  } finally {
    dispatch(flagGettingCustomerUpdateHistory(false));
  }
};

const putCustomerUpdateHistory =
  async ({ ticketId, payload }) =>
  async (dispatch) => {
    try {
      const { id: customerUpdateHistoryId } = payload;
      dispatch(flagGettingCustomerUpdateHistory(true));
      const url = generatePutCustomerUpdateHistoryEndpoint(
        customerUpdateHistoryId,
      );
      const response = await fetch(url, {
        ...DEFAULT_PUT_CONFIG,
        body: JSON.stringify(payload),
      });
      await isErrorResponse(response, null, dispatch);
      dispatch(getTicket({ ticketId }));
      toastr.success(
        'Éxito',
        'Historial de actualización del cliente actualizado correctamente',
      );
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagGettingCustomerUpdateHistory(false));
    }
  };

const flagGettingTicketFromItinerary = (payload) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_TICKET_FROM_ITINERARY,
    payload,
  });

const clearTicketFromItinerary = () => (dispatch) =>
  dispatch({
    type: CLEAR_TICKET_FROM_ITINERARY,
  });

const getTicketFromItinerary = async (itineraryId) => async (dispatch) => {
  try {
    dispatch(flagGettingTicketFromItinerary(true));
    const url = `${TICKET_ENDPOINT}/itinerary/${itineraryId}`;
    const response = await fetch(url, DEFAULT_GET_CONFIG);
    await isErrorResponse(response, null, dispatch);
    const ticket = await response.json();
    dispatch({
      type: GET_TICKET_FROM_ITINERARY,
      payload: ticket,
    });
  } catch (error) {
    toastr.error('Error', error.message);
  } finally {
    dispatch(flagGettingTicketFromItinerary(false));
  }
};

const getCheckIfTicketExists =
  ({ documentSeries, documentCode }) =>
  async (dispatch) => {
    try {
      const query = {
        documentSeries,
        documentCode,
      };
      dispatch(flagGettingTickets(true));
      const url = `${TICKET_ENDPOINT}/exists?${QueryString.stringify(query)}`;
      const response = await fetch(url, DEFAULT_GET_CONFIG);
      await isErrorResponse(response, null, dispatch);
      const ticket = response.json();
      return ticket;
    } catch (error) {
      toastr.error('Error', error.message);
      return null;
    } finally {
      dispatch(flagGettingTickets(false));
    }
  };

export {
  getTickets,
  clearTickets,
  getTicket,
  clearTicket,
  voidTicket,
  getTicketDeleteModal,
  executeTicketVoidAction,
  registerPinPadVoid,
  getPrintedTicket,
  postChangePassenger,
  postSendTicketByEmail,
  getSalesSessionTickets,
  postTicketVoucherTypeChange,
  postTicketRefund,
  getTicketCustomer,
  patchTicketTaxableInformation,
  getTicketsForMassivePostpone,
  postTicketMassivePostpone,
  getTicketActionModal,
  postPostponeTicket,
  getTicketsV2,
  voidTicketWithCallback,
  putAcountingData,
  postTicketMassivePostponeV2,
  postMassiveTicketActionModal,
  postImmediateTicketPostpone,
  flagTicketActivity,
  getCustomerUpdateHistory,
  clearCustomerUpdateHistory,
  putCustomerUpdateHistory,
  putTicketChangeLocation,
  getTicketFromItinerary,
  clearTicketFromItinerary,
  getCheckIfTicketExists,
  flagPrintTicketVoucher,
};
